
import AdUnit from '../components/AdUnit';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, BedDouble, Compass, ShieldCheck, Smartphone, Plane, Wallet, Car, Tag, Loader2, Percent, Sparkles, Zap, Clock } from 'lucide-react';
import { useMonetization } from '../context/MonetizationContext';
import { PageHero } from '../components/shared';
import useReveal from '../hooks/useReveal';
import { trackAffiliateClick, fetchDeals } from '../api';

const CAT_ICON = {
  hotels: BedDouble,
  tours: Compass,
  insurance: ShieldCheck,
  esim: Smartphone,
  flights: Plane,
  finance: Wallet,
  transfers: Car,
  other: Tag,
};
const CAT_LABEL = {
  hotels: 'Hotels & stays',
  tours: 'Tours & experiences',
  insurance: 'Travel insurance',
  esim: 'eSIM & connectivity',
  flights: 'Flights',
  finance: 'Money & cards',
  transfers: 'Airport transfers & car hire',
  other: 'More travel essentials',
};
const CAT_ORDER = ['hotels', 'flights', 'tours', 'esim', 'insurance', 'transfers', 'finance', 'other'];

const DealCard = ({ a }) => {
  const Icon = CAT_ICON[a.category] || Tag;
  return (
    <a
      data-testid={`deal-card-${a.id}`}
      href={a.url || '#'}
      target="_blank"
      rel="sponsored noopener noreferrer"
      onClick={() => trackAffiliateClick(a.id)}
      className={`lift group relative flex flex-col rounded-2xl bg-white p-5 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)] ${a.badge ? 'border-2 border-[#B68A35] ring-1 ring-[#B68A35]/20' : 'border border-[#E6D7A2]'}`}
    >
      {a.badge && (
        <span data-testid={`deal-badge-${a.id}`} className="deal-ribbon absolute -top-3 right-4 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide uppercase rounded-full sunset-gradient text-white px-3 py-1.5 shadow-[0_8px_18px_-6px_rgba(182,138,53,0.7)]">
          <Sparkles className="w-3.5 h-3.5" /> {a.badge}
        </span>
      )}
      <div className="flex items-center gap-2.5">
        <span className="flex items-center justify-center w-10 h-10 rounded-xl sunset-gradient text-white shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <span className="font-display text-[18px] font-semibold text-[#1B1D21] leading-tight">{a.name}</span>
      </div>
      <p className="mt-3 text-[14px] leading-relaxed text-[#4B4F58] grow">{a.blurb}</p>
      <div className="mt-4 flex items-center justify-between gap-3">
        {a.priceLabel ? <span className="text-[13px] font-semibold text-[#2F663C]">{a.priceLabel}</span> : <span />}
        <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#92722A] group-hover:text-[#1B1D21] transition-colors">
          {a.cta || 'View deal'} <ExternalLink className="w-3.5 h-3.5" />
        </span>
      </div>
    </a>
  );
};

// ---- Live, imported deals (real prices, one click to the booking page) ----
const LiveDealCard = ({ d }) => {
  const Icon = CAT_ICON[d.category] || Tag;
  return (
    <a
      data-testid={`live-deal-${d.id}`}
      href={d.url}
      target="_blank"
      rel="sponsored noopener noreferrer"
      onClick={() => trackAffiliateClick(d.id)}
      className={`lift group relative flex flex-col rounded-2xl bg-white p-5 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)] ${d.badge ? 'border-2 border-[#2F663C] ring-1 ring-[#2F663C]/20' : 'border border-[#E6D7A2]'}`}
    >
      {d.badge && (
        <span className="absolute -top-3 right-4 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide uppercase rounded-full bg-[#2F663C] text-white px-3 py-1.5 shadow-[0_8px_18px_-6px_rgba(31,138,143,0.7)]">
          <Zap className="w-3.5 h-3.5" /> {d.badge}
        </span>
      )}
      {d.image && <img src={d.image} alt="" loading="lazy" className="w-full h-36 object-cover rounded-xl mb-4" />}
      <div className="flex items-center gap-2.5">
        <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#1B1D21] text-[#CBA344] shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <span className="font-display text-[18px] font-semibold text-[#1B1D21] leading-tight">{d.name}</span>
      </div>
      <p className="mt-3 text-[14px] leading-relaxed text-[#4B4F58] grow">{d.blurb}</p>
      <div className="mt-4 flex items-end justify-between gap-3">
        <span className="font-display text-[22px] font-bold text-[#2F663C] leading-none">{d.priceLabel}</span>
        <span className="inline-flex items-center gap-1.5 rounded-full sunset-gradient text-white text-[13px] font-semibold px-4 py-2 group-hover:brightness-110 transition">
          {d.cta || 'Book now'} <ExternalLink className="w-3.5 h-3.5" />
        </span>
      </div>
    </a>
  );
};

const LiveDeals = () => {
  const [state, setState] = useState({ loading: true, deals: [], updatedAt: '' });
  useEffect(() => {
    let alive = true;
    fetchDeals()
      .then((r) => alive && setState({ loading: false, deals: r.deals || [], updatedAt: r.updatedAt || '' }))
      .catch(() => alive && setState({ loading: false, deals: [], updatedAt: '' }));
    return () => { alive = false; };
  }, []);
  if (state.loading) return null;
  if (!state.deals.length) return null;
  const groups = CAT_ORDER
    .map((cat) => ({ cat, items: state.deals.filter((d) => d.category === cat) }))
    .filter((g) => g.items.length);
  const when = state.updatedAt ? new Date(state.updatedAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '';
  return (
    <section className="mt-10 reveal" data-testid="live-deals">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg sunset-gradient text-white shrink-0"><Zap className="w-5 h-5" /></span>
          <h2 className="font-display text-[26px] font-semibold text-[#1B1D21]">Live deals — one click to book</h2>
        </div>
        {when && <span className="inline-flex items-center gap-1.5 text-[12px] text-[#4B4F58]"><Clock className="w-3.5 h-3.5" /> Prices refreshed {when} AWST</span>}
      </div>
      <div className="space-y-10">
        {groups.map(({ cat, items }) => (
          <div key={cat}>
            <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#92722A] mb-3">{CAT_LABEL[cat] || cat}</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((d) => <LiveDealCard key={d.id} d={d} />)}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[12px] text-[#797E86]">Fares are the lowest seen by other travellers in the last 48 hours and can change by the time you click through. Prices in AUD, return, taxes included.</p>
    </section>
  );
};

const Deals = () => {
  const { affiliates = [], ready } = useMonetization();
  useReveal([affiliates.length, ready]);

  const grouped = CAT_ORDER
    .map((cat) => ({ cat, items: affiliates.filter((a) => (a.category || 'other') === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="bg-[#F6F1E3] min-h-screen">
      <PageHero
        image="https://images.unsplash.com/photo-1698923824785-8964e4af18d6"
        alt="Burj Al Arab and Dubai skyline on a clear blue-sky day"
        label="Save on your trip"
        title="UAE travel deals"
        subtitle="Our hand-picked partners for hotels, flights, eSIMs, tours and more — in one place, so you can book smart and save."
      />
      <AdUnit slot="inline" />

      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="reveal rounded-xl border-l-4 border-[#CBA344] bg-[#F2ECCF] px-5 py-4 text-[14px] leading-relaxed text-[#5D3B26]">
          <span className="font-semibold text-[#1B1D21]">Affiliate disclosure:</span> Some links below are affiliate links — if you book through them we may earn a small commission at no extra cost to you. This never affects what we recommend.{' '}
          <Link to="/about" className="font-semibold underline underline-offset-2 text-[#92722A]">Full disclosure</Link>.
        </div>

        <LiveDeals />

        {!ready ? (
          <div className="py-24 flex justify-center text-[#92722A]"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : grouped.length === 0 ? (
          <div data-testid="deals-empty" className="py-20 text-center rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2] mt-8">
            <Percent className="w-8 h-8 mx-auto text-[#92722A]" />
            <p className="mt-3 text-[16px] text-[#4B4F58]">No live deals right now — check back soon for fresh UAE travel offers.</p>
          </div>
        ) : (
          <div className="mt-14 space-y-14" data-testid="deals-list">
            <h2 className="font-display text-[22px] font-semibold text-[#1B1D21] -mb-6">Our trusted partners</h2>
            {grouped.map(({ cat, items }) => {
              const Icon = CAT_ICON[cat] || Tag;
              return (
                <section key={cat} className="reveal">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#1B1D21] text-[#CBA344] shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <h2 className="font-display text-[26px] font-semibold text-[#1B1D21]">{CAT_LABEL[cat] || 'Travel essentials'}</h2>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((a) => <DealCard key={a.id} a={a} />)}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Deals;
